import {IServiceFactoryExtended} from "../../../service-factory-extended.interface";
import {IServicesConfigurations} from "../../../configurations/config-data-sections/services-configurations";
import {IGeoComplyFailure, IGeoComplyResponse, IGeoLocationStrategyRunParams} from "../geo-location.strategy.interface";
import {IScriptLoaderService} from "../../../scrip-loader/script-loader.service.interface";
import {ILoggerService} from "../../../logger/logger.service.interface";
import {GeoComplyFailureReason} from "../geo-comply-error-codes";
import {GeoAnalyticsEvent, GeoLocationAnalytics} from "../../geo-location-analytics";


interface IGeoComplyCreatePcClientParams {
    onConnectSuccess: () => void;
    onConnectFailed: (errorCode: number, errorMessage: string) => void;
    onGeolocationAvailable: (result: string) => void;
    onGeolocationFailed: (errorCode: number, errorMessage: string) => void;
}

interface IGeoComplyPcClientFactory {
    createClient(params: IGeoComplyCreatePcClientParams): IGeoComplyPcClient;
}

interface IGeoComplyPcClient {
    connect(installerId: string, environment: string): void;
    disconnect(): void;
    requestGeolocation(): void;
    setLicense(licenceKey: string): void;
    customFields: {
        set: (key: string, value: string) => void;
    }
    setUserPhoneNumber: string; //TODO - why this is not a function ?
    setGeolocationReason(reason: string): void;
    setUserId(userId: string): void;
    getLastErrorCode(): string;
}


class GeoComplyPcScriptLoader {
    private static _loadScriptPromise: Promise<void> | null = null;
    static async LoadGeoComplyLib( url: string, scriptLoader: IScriptLoaderService, logger: ILoggerService) {
        if(!GeoComplyPcScriptLoader._loadScriptPromise) {
            GeoComplyPcScriptLoader._loadScriptPromise = scriptLoader.loadScript({
                url: url,
                scriptType: "module"
            });
        }

        try {
            await GeoComplyPcScriptLoader._loadScriptPromise
        } catch (err) {
            logger.error("Failed to load Geo Comply library", err);
            GeoComplyPcScriptLoader._loadScriptPromise = null;
            throw err;
        }
    }
}

export class GeoComplyPcClient {
    constructor(private readonly services: IServiceFactoryExtended) {
        this._geoComplyPcClientPromise = this._loadGeoComplyClient();
    }

    protected readonly _geoComplyPcClientPromise: Promise<IGeoComplyPcClient>;

    private _getGeoComplyPcClient(): Promise<IGeoComplyPcClient> {
        return this._geoComplyPcClientPromise;
    }

    private onConnectResolver: null | ((result: IGeoComplyFailure | null) => void) = null;
    private onGeolocationResolver: null | ((result: IGeoComplyResponse) => void) = null;

    private get configs(): IServicesConfigurations["GeoLocation"]["PC"] {
        return this.services.configuration.servicesConfigs.GeoLocation.PC;
    }

    private async _loadGeoComplyClient(): Promise<IGeoComplyPcClient> {

        await GeoComplyPcScriptLoader.LoadGeoComplyLib(this.configs.geoLib, this.services.scriptLoader, this.services.logger);

        const geoComplyClientFactory = this.services.window.getGlobalProperty('GeoComply') as IGeoComplyPcClientFactory;
        if(!geoComplyClientFactory) {
            throw new Error(`Geo Comply PC library didn't create GeoComply object on window`);
        }


        return geoComplyClientFactory.createClient({
            onConnectSuccess: () => {
                if(this.onConnectResolver) {
                    this.onConnectResolver(null);
                }
                this.onConnectResolver = null;
                this._isConnected = true;
            },
            onConnectFailed: (errorCode, errorMessage) => {
                if(this.onConnectResolver) {
                    this.onConnectResolver({
                        geoComplyError: {
                            errorCode: errorCode,
                            errorMessage: errorMessage
                        },
                        additionalFailureReason: GeoComplyFailureReason.TechnicalProblem
                    });
                }
                this.onConnectResolver = null;
                this._isConnected = false;
            },
            onGeolocationAvailable: (data) => {
                if(this.onGeolocationResolver) {
                    this.onGeolocationResolver({
                        data: data
                    });
                }
                this.onGeolocationResolver = null;
            },
            onGeolocationFailed: (errorCode, errorMessage) => {
                if(this.onGeolocationResolver) {
                    this.onGeolocationResolver({
                        failure: {
                            geoComplyError: {
                                errorCode: errorCode,
                                errorMessage: errorMessage
                            },
                            additionalFailureReason: GeoComplyFailureReason.TechnicalProblem
                        }
                    });
                }

                this.onGeolocationResolver = null;
            }
        });
    }


    private _isConnected: boolean = false;
    async connect(): Promise<IGeoComplyFailure | null> {
        if(this._isConnected) {
            return null;
        }

        const client = await this._getGeoComplyPcClient();
        return await this._executeGeoComplyConnect(client);
    }

    private get _geoComplyProcessTimeoutInMS(): number {
        return this.services.configuration.servicesConfigs.GeoLocation.geoProcessTimeout;
    }

    private _executeGeoComplyConnect(client: IGeoComplyPcClient): Promise<IGeoComplyFailure | null> {
        return new Promise<IGeoComplyFailure | null>((resolve) => {
            const timeoutRef = setTimeout(() => {
                this.onConnectResolver = null;
                resolve({
                    additionalFailureReason: GeoComplyFailureReason.GeoComplyTimeout
                });
            }, this._geoComplyProcessTimeoutInMS);

            this.onConnectResolver = (result: IGeoComplyFailure | null) => {
                clearTimeout(timeoutRef);
                resolve(result);
            };
            client.connect(this.configs.GCinstallerID, this.configs.GCenvironment);
        });
    }

    async disconnect(): Promise<void> {
        if(this._isConnected) {
            (await this._getGeoComplyPcClient()).disconnect();
        }
    }

    async runGeoLocation(params: IGeoLocationStrategyRunParams): Promise<IGeoComplyResponse> {
        const connectFailure = await this.connect();
        if(connectFailure) {
            return {
                failure: connectFailure
            }
        }

        const client = await this._getGeoComplyPcClient();
        client.setLicense(params.licenseKey);
        client.customFields.set('session_key', params.locationTransactionId);
        //client.setUserPhoneNumber = params.mobileNumber;
        client.setGeolocationReason(params.reason)
        client.setUserId(this.services.user.cid.toString());
        return await this._executeGeoComplyLocation(client, params.analytics);
    }

    private _executeGeoComplyLocation(client: IGeoComplyPcClient, analytics: GeoLocationAnalytics): Promise<IGeoComplyResponse> {
        return new Promise<IGeoComplyResponse>((resolve) => {

            const timeoutRef = setTimeout(() => {
                this.onGeolocationResolver = null;
                analytics.reportEvent({
                    geoEventName: GeoAnalyticsEvent.GeoComplyTimeout
                });
                resolve({
                    failure: {
                        additionalFailureReason: GeoComplyFailureReason.GeoComplyTimeout
                    }
                });
            }, this._geoComplyProcessTimeoutInMS);

            this.onGeolocationResolver = (result: IGeoComplyResponse) => {
                clearTimeout(timeoutRef);
                resolve(result);
            };
            client.requestGeolocation();
        });
    }
}
